import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowRight } from "lucide-react";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Brand from "../components/Brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  function homeFor(role) {
    if (role === "admin" || role === "sales_employee") return "/crm";
    if (role === "technician") return "/fsm";
    if (role === "sc") return "/sc";
    if (role === "service_team") return "/service";
    return "/app";
  }

  async function submit(e) {
    e?.preventDefault();
    setLoading(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      const target = loc.state?.from?.pathname || homeFor(data.role);
      nav(target, { replace: true });
      toast.success(`Welcome, ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F5F0] crm-grid relative">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left brand panel */}
        <div className="hidden lg:flex flex-col justify-between p-12 bg-[#FFFCF7] text-[#0F2A14] relative overflow-hidden border-r border-[#E8E2D6]">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#FFF4E5] blur-3xl"></div>
          <div className="absolute right-12 bottom-24 w-72 h-72 rounded-full bg-[#E8F5E9] blur-3xl"></div>
          <div className="relative z-10">
            <Brand variant="full" to="/" size={44} />
          </div>
          <div className="relative z-10">
            <h1 className="font-display text-6xl xl:text-7xl leading-[0.95] font-black">
              From rooftop<br />enquiry<br />
              <span className="italic font-bold text-[#F39C12]">to subsidy.</span>
            </h1>
            <p className="mt-8 text-[#475569] font-mono text-sm leading-relaxed max-w-md">
              One source of truth for backend teams, field technicians, and end customers — built for India&apos;s solar
              installers.
            </p>
          </div>
          <div className="relative z-10 grid grid-cols-3 gap-6 font-mono text-xs uppercase tracking-widest text-[#475569]">
            <div><div className="text-3xl font-display text-[#1B5E20] font-bold">5</div>Phase pipeline</div>
            <div><div className="text-3xl font-display text-[#1B5E20] font-bold">3</div>Personas</div>
            <div><div className="text-3xl font-display text-[#1B5E20] font-bold">∞</div>Field tickets</div>
          </div>
        </div>

        {/* Right form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <Brand variant="full" to="/" size={36} />
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#475569]">Step 01 / Sign in</div>
            <h2 className="font-display text-4xl font-bold text-[#0F2A14] mt-2">Welcome back.</h2>
            <p className="text-sm text-[#475569] mt-2">
              Use your email &amp; password.{" "}
              <span className="text-[#0F2A14]">New customer?</span>{" "}
              <Link to="/signup" data-testid="signup-link" className="underline decoration-[#F39C12] underline-offset-4 text-[#1B5E20]">
                Create an account.
              </Link>
            </p>
            <p className="text-xs font-mono text-[#475569] mt-4">
              Solar Consultant?{" "}
              <Link to="/sc/signup" data-testid="login-sc-link" className="underline decoration-[#F39C12] underline-offset-4 text-[#1B5E20] font-semibold">
                Continue with mobile + OTP →
              </Link>
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-[#475569] mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full bg-transparent border-b-2 border-[#0F2A14] py-3 text-[#0F2A14] placeholder-[#94A3B8] focus:outline-none focus:border-[#F39C12]"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-[#475569] mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full bg-transparent border-b-2 border-[#0F2A14] py-3 text-[#0F2A14] placeholder-[#94A3B8] focus:outline-none focus:border-[#F39C12]"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="btn-tactile w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-widest hover:bg-[#0F3D14] transition-colors duration-200 flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign in"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-right">
                <Link to="/reset-password" data-testid="login-forgot-link" className="text-xs font-medium uppercase tracking-wider text-[#475569] hover:text-[#1B5E20]">
                  Forgot password?
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
